/**
 * TypeScript wrapper for WASM Bezier curve math.
 *
 * Mirrors the signatures of the original `modeling/curvemath.ts` so existing
 * callers don't need to change. Internally packs `CurvePoint[]` into a flat
 * `Float32Array` with stride 11 (co + handleLeft + handleRight + tilt + radius)
 * and the per-point handle-type byte pairs (left, right) into a `Uint8Array`.
 */

import type {
  CurveData,
  CurveHandleType,
  CurvePoint,
} from "../modeling/types.ts";
import {
  compute_rmf_raw as wasmComputeRmfRaw,
  enforce_handle_constraints_batch as wasmEnforceHandleConstraintsBatch,
  eval_bezier_segment as wasmEvalBezierSegment,
  eval_bezier_tangent as wasmEvalBezierTangent,
  evaluate_curve as wasmEvaluateCurve,
  subdivide_curve_segment as wasmSubdivideCurveSegment,
} from "./pkg/poseref_wasm.js";

export type Vec3 = [number, number, number];

export interface CurveFrame {
  position: Vec3;
  tangent: Vec3;
  normal: Vec3;
  binormal: Vec3;
}

export interface CurveEvaluation {
  positions: Vec3[];
  frames: CurveFrame[];
  radii: number[];
  tilts: number[];
}

// ---------------------------------------------------------------------------
// Layout constants — must match curve_math.rs.
// ---------------------------------------------------------------------------

const POINT_STRIDE = 11;
const HANDLE_TYPE_STRIDE = 2;
const FRAME_STRIDE = 9;

const HANDLE_TYPE_CODE: Record<CurveHandleType, number> = {
  auto: 0,
  vector: 1,
  aligned: 2,
  free: 3,
};

const MOVED_CO = 0;
const MOVED_LEFT = 1;
const MOVED_RIGHT = 2;

// ---------------------------------------------------------------------------
// Packing
// ---------------------------------------------------------------------------

function packPoints(points: CurvePoint[]): Float32Array {
  const buf = new Float32Array(points.length * POINT_STRIDE);
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const o = i * POINT_STRIDE;
    buf[o] = p.co[0];
    buf[o + 1] = p.co[1];
    buf[o + 2] = p.co[2];
    buf[o + 3] = p.handleLeft[0];
    buf[o + 4] = p.handleLeft[1];
    buf[o + 5] = p.handleLeft[2];
    buf[o + 6] = p.handleRight[0];
    buf[o + 7] = p.handleRight[1];
    buf[o + 8] = p.handleRight[2];
    buf[o + 9] = p.tilt;
    buf[o + 10] = p.radius;
  }
  return buf;
}

function packHandleTypes(points: CurvePoint[]): Uint8Array {
  const buf = new Uint8Array(points.length * HANDLE_TYPE_STRIDE);
  for (let i = 0; i < points.length; i++) {
    buf[i * HANDLE_TYPE_STRIDE] = HANDLE_TYPE_CODE[points[i].handleTypeLeft];
    buf[i * HANDLE_TYPE_STRIDE + 1] =
      HANDLE_TYPE_CODE[points[i].handleTypeRight];
  }
  return buf;
}

/** Write the result of a batched enforce back into the `CurvePoint[]`. */
function unpackPoints(points: CurvePoint[], buf: Float32Array): void {
  for (let i = 0; i < points.length; i++) {
    const o = i * POINT_STRIDE;
    const p = points[i];
    p.co = [buf[o], buf[o + 1], buf[o + 2]];
    p.handleLeft = [buf[o + 3], buf[o + 4], buf[o + 5]];
    p.handleRight = [buf[o + 6], buf[o + 7], buf[o + 8]];
    p.tilt = buf[o + 9];
    p.radius = buf[o + 10];
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/** Evaluate a cubic Bezier segment at parameter t via De Casteljau. */
export function evalBezierSegment(
  p0: Vec3,
  h0: Vec3,
  h1: Vec3,
  p1: Vec3,
  t: number,
): Vec3 {
  const r = wasmEvalBezierSegment(
    p0[0],
    p0[1],
    p0[2],
    h0[0],
    h0[1],
    h0[2],
    h1[0],
    h1[1],
    h1[2],
    p1[0],
    p1[1],
    p1[2],
    t,
  );
  return [r[0], r[1], r[2]];
}

/** Evaluate the tangent (first derivative) of a cubic Bezier at t. */
export function evalBezierTangent(
  p0: Vec3,
  h0: Vec3,
  h1: Vec3,
  p1: Vec3,
  t: number,
): Vec3 {
  const r = wasmEvalBezierTangent(
    p0[0],
    p0[1],
    p0[2],
    h0[0],
    h0[1],
    h0[2],
    h1[0],
    h1[1],
    h1[2],
    p1[0],
    p1[1],
    p1[2],
    t,
  );
  return [r[0], r[1], r[2]];
}

/**
 * Evaluate the full curve. Preserves the degenerate-case shortcuts of the
 * original TS implementation: 0 or 1 points return a single-sample identity
 * frame so callers can draw a gizmo without special-casing.
 */
export function evaluateCurve(curve: CurveData): CurveEvaluation {
  const { points, resolutionU, cyclic } = curve;

  if (points.length === 0) {
    return {
      positions: [[0, 0, 0]],
      frames: [
        {
          position: [0, 0, 0],
          tangent: [0, 1, 0],
          normal: [1, 0, 0],
          binormal: [0, 0, 1],
        },
      ],
      radii: [1],
      tilts: [0],
    };
  }
  if (points.length === 1) {
    const p = points[0];
    return {
      positions: [p.co],
      frames: [
        {
          position: p.co,
          tangent: [0, 1, 0],
          normal: [1, 0, 0],
          binormal: [0, 0, 1],
        },
      ],
      radii: [p.radius],
      tilts: [p.tilt],
    };
  }

  const packed = packPoints(points);
  const result = wasmEvaluateCurve(packed, resolutionU, cyclic);
  const positionsFlat = result.positions;
  const framesFlat = result.frames;
  const radii = Array.from(result.radii) as number[];
  const tilts = Array.from(result.tilts) as number[];
  result.free();

  const sampleCount = radii.length;
  const positions: Vec3[] = new Array(sampleCount);
  const frames: CurveFrame[] = new Array(sampleCount);
  for (let i = 0; i < sampleCount; i++) {
    const po = i * 3;
    const pos: Vec3 = [
      positionsFlat[po],
      positionsFlat[po + 1],
      positionsFlat[po + 2],
    ];
    positions[i] = pos;
    const fo = i * FRAME_STRIDE;
    frames[i] = {
      position: pos,
      tangent: [framesFlat[fo], framesFlat[fo + 1], framesFlat[fo + 2]],
      normal: [framesFlat[fo + 3], framesFlat[fo + 4], framesFlat[fo + 5]],
      binormal: [framesFlat[fo + 6], framesFlat[fo + 7], framesFlat[fo + 8]],
    };
  }

  return { positions, frames, radii, tilts };
}

/**
 * Compute Rotation-Minimizing Frames along a polyline with pre-computed
 * tangents. The skin-mesh generator calls the Rust RMF kernel directly
 * (`skin_geometry.rs` → `curve_math::compute_rmf`) to avoid round-tripping
 * large chains through the JS↔WASM boundary; this wrapper remains for
 * external callers and the isolated RMF correctness tests.
 */
export function computeRMF(positions: Vec3[], tangents: Vec3[]): CurveFrame[] {
  const n = positions.length;
  if (n === 0) return [];

  const posBuf = new Float32Array(n * 3);
  const tanBuf = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const p = positions[i];
    const t = tangents[i];
    posBuf[i * 3] = p[0];
    posBuf[i * 3 + 1] = p[1];
    posBuf[i * 3 + 2] = p[2];
    tanBuf[i * 3] = t[0];
    tanBuf[i * 3 + 1] = t[1];
    tanBuf[i * 3 + 2] = t[2];
  }

  const result = wasmComputeRmfRaw(posBuf, tanBuf);
  const framesFlat = result.frames;
  result.free();

  const frames: CurveFrame[] = new Array(n);
  for (let i = 0; i < n; i++) {
    const fo = i * FRAME_STRIDE;
    frames[i] = {
      position: positions[i],
      tangent: [framesFlat[fo], framesFlat[fo + 1], framesFlat[fo + 2]],
      normal: [framesFlat[fo + 3], framesFlat[fo + 4], framesFlat[fo + 5]],
      binormal: [framesFlat[fo + 6], framesFlat[fo + 7], framesFlat[fo + 8]],
    };
  }
  return frames;
}

/**
 * Enforce handle constraints for a single point after its control point or
 * handles moved. Thin wrapper over the batched variant.
 */
export function enforceHandleConstraints(
  points: CurvePoint[],
  pointIndex: number,
  cyclic: boolean,
  movedComponent?: "co" | "handleLeft" | "handleRight",
): void {
  enforceHandleConstraintsBatch(
    points,
    [pointIndex],
    cyclic,
    movedComponent ? [movedComponent] : undefined,
  );
}

/**
 * Batched entry point: enforce handle constraints for every index in
 * `changedIndices` in one WASM call. Used by store.ts after physics sim
 * write-back and by curveOps.ts during live edit.
 */
export function enforceHandleConstraintsBatch(
  points: CurvePoint[],
  changedIndices: number[] | readonly number[],
  cyclic: boolean,
  changedComponents?: ReadonlyArray<
    "co" | "handleLeft" | "handleRight" | undefined
  >,
): void {
  if (changedIndices.length === 0) return;

  const packed = packPoints(points);
  const types = packHandleTypes(points);
  const indices = new Uint32Array(changedIndices.length);
  for (let i = 0; i < changedIndices.length; i++)
    indices[i] = changedIndices[i];

  const components = new Uint8Array(changedIndices.length);
  if (changedComponents) {
    for (let i = 0; i < changedIndices.length; i++) {
      const c = changedComponents[i];
      components[i] =
        c === "handleLeft"
          ? MOVED_LEFT
          : c === "handleRight"
            ? MOVED_RIGHT
            : MOVED_CO;
    }
  } // else all MOVED_CO (zero-init)

  wasmEnforceHandleConstraintsBatch(packed, types, indices, components, cyclic);
  unpackPoints(points, packed);
}

/** Subdivide a Bezier segment at t=0.5. */
export function subdivideCurveSegment(
  points: CurvePoint[],
  segIdx: number,
): {
  newPoint: CurvePoint;
  updatedLeftHandleRight: Vec3;
  updatedRightHandleLeft: Vec3;
} {
  const packed = packPoints(points);
  const result = wasmSubdivideCurveSegment(packed, segIdx);
  const np = result.new_point;
  const ulh = result.updated_left_handle_right;
  const urh = result.updated_right_handle_left;
  result.free();

  return {
    newPoint: {
      co: [np[0], np[1], np[2]],
      handleLeft: [np[3], np[4], np[5]],
      handleRight: [np[6], np[7], np[8]],
      handleTypeLeft: "aligned",
      handleTypeRight: "aligned",
      tilt: np[9],
      radius: np[10],
    },
    updatedLeftHandleRight: [ulh[0], ulh[1], ulh[2]],
    updatedRightHandleLeft: [urh[0], urh[1], urh[2]],
  };
}
